import { useState, type FormEvent } from 'react'
import { Link, useParams } from 'react-router'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Download, Eye, FileArchive, FileText, Send, Shuffle, XCircle } from 'lucide-react'
import {
  DISTRIBUTION_MODES,
  STAGE_STATUSES,
  type DistributionMode,
  type StageStatus,
} from '@concours/shared'
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  ConfirmModal,
  DocumentPreviewModal,
  Empty,
  Input,
  PageHeader,
  Select,
  Stat,
  StatGrid,
  Table,
  useAuth,
  useCan,
  useToast,
  type Column,
} from '@concours/ui'
import { errorMessage, fieldErrors } from '../../lib/errors'
import { when } from '../../lib/competition-types'
import {
  BATCH_LABELS,
  CONVOCATION_VARIANT,
  DOCUMENTS_KEY,
  fromLocalInput,
  saveFile,
  toLocalInput,
  useCenters,
  useConvocations,
  useDocumentsHub,
  useStageDocuments,
  type Batch,
  type ConvocationRow,
  type OfficialDocument,
} from '../../lib/documents'
import { Progress } from '../grading/Progress'

const MODE_OPTIONS = Object.entries(DISTRIBUTION_MODES).map(([value, label]) => ({
  value,
  label,
}))

/**
 * Convocations et documents d'une étape (cahier §9.2, §10) : répartition dans
 * les salles, génération (aperçu, lot ZIP), envoi, annulation motivée, liste
 * d'émargement, PV de commission, liste officielle des résultats.
 */
export function StageDocumentsPage() {
  const { stageId = '' } = useParams()
  const hub = useDocumentsHub()
  const competition = hub.data?.find((c) => c.stages.some((s) => s.id === stageId))
  const stage = competition?.stages.find((s) => s.id === stageId)
  const scope = { type: 'competition' as const, id: competition?.id ?? '' }
  const canGenerate = useCan('convocations.generate', scope)
  const canSend = useCan('convocations.send', scope)
  const canPv = useCan('pv.generate', scope)
  const canResults = useCan('results.view', scope)

  if (hub.isError) return <Alert tone="danger">{errorMessage(hub.error)}</Alert>
  if (hub.isPending)
    return (
      <Card>
        <CardBody>Chargement…</CardBody>
      </Card>
    )
  if (!competition || !stage)
    return <Alert tone="danger">Étape introuvable ou hors de votre périmètre.</Alert>

  return (
    <>
      <PageHeader
        eyebrow={
          <Link to="/documents" className="link">
            Convocations et documents
          </Link>
        }
        title={`${stage.label} — ${competition.title}`}
        description={
          <>
            <span className="mono">{competition.reference}</span> · étape {stage.position} ·{' '}
            {STAGE_STATUSES[stage.status as StageStatus] ?? stage.status} · prévue le{' '}
            {when(stage.startsAt)}
          </>
        }
      />
      <div className="stack" style={{ gap: 'var(--space-6)' }}>
        {(canGenerate || canSend) && (
          <ConvocationsSection
            stageId={stageId}
            competitionId={competition.id}
            canGenerate={canGenerate}
            canSend={canSend}
          />
        )}
        <StageDocumentsSection
          stageId={stageId}
          canGenerate={canGenerate}
          canPv={canPv}
          canResults={canResults}
        />
      </div>
    </>
  )
}

function ConvocationsSection({
  stageId,
  competitionId,
  canGenerate,
  canSend,
}: {
  stageId: string
  competitionId: string
  canGenerate: boolean
  canSend: boolean
}) {
  const { session } = useAuth()
  const toast = useToast()
  const queryClient = useQueryClient()
  const overview = useConvocations(stageId)
  const centers = useCenters(competitionId)
  const o = overview.data
  const [mode, setMode] = useState<DistributionMode>('alphabetical')
  const [date, setDate] = useState<string | null>(null)
  const [chosen, setChosen] = useState<string[] | null>(null)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [modal, setModal] = useState<null | 'issue' | 'cancel' | 'redistribute'>(null)
  const [preview, setPreview] = useState<{
    row: ConvocationRow
    blob: Blob | null
    error?: string
  } | null>(null)

  const scheduled = date ?? toLocalInput(o?.stage.startsAt)
  const selected = chosen ?? centers.data?.map((c) => c.id) ?? []
  const capacity = (centers.data ?? [])
    .filter((c) => selected.includes(c.id))
    .reduce((n, c) => n + c.capacity, 0)

  const invalidate = () => queryClient.invalidateQueries({ queryKey: DOCUMENTS_KEY })
  const distribute = useMutation({
    mutationFn: () =>
      session.api.post<{ convocations: number }>(
        `/admin/stages/${stageId}/convocations/distribute`,
        {
          mode,
          scheduledAt: scheduled ? fromLocalInput(scheduled) : '',
          centerIds: selected,
        }
      ),
    onSuccess: async (r) => {
      toast.success(`${r.convocations} candidat(s) répartis`)
      setErrors({})
      setModal(null)
      await invalidate()
    },
    onError: (error) => {
      setModal(null)
      setErrors(fieldErrors(error))
      toast.error(errorMessage(error))
    },
  })
  const batch = useMutation({
    mutationFn: () => session.api.post<Batch>(`/admin/stages/${stageId}/convocations/batch`),
    onSuccess: async () => {
      toast.success('Génération des convocations lancée')
      await invalidate()
    },
    onError: (error) => toast.error(errorMessage(error)),
  })
  const issue = useMutation({
    mutationFn: () =>
      session.api.post<{ issued: number }>(`/admin/stages/${stageId}/convocations/issue`),
    onSuccess: async (r) => {
      toast.success(`${r.issued} convocation(s) envoyée(s) aux candidats`)
      setModal(null)
      await invalidate()
    },
    onError: (error) => {
      setModal(null)
      toast.error(errorMessage(error))
    },
  })
  const cancel = useMutation({
    mutationFn: (reason: string) =>
      session.api.post<{ cancelled: number }>(`/admin/stages/${stageId}/convocations/cancel`, {
        reason,
      }),
    onSuccess: async (r) => {
      toast.success(`${r.cancelled} convocation(s) annulée(s)`)
      setModal(null)
      await invalidate()
    },
    onError: (error) => toast.error(errorMessage(error)),
  })
  const list = useMutation({
    mutationFn: async () => {
      const doc = await session.api.post<OfficialDocument>(
        `/admin/stages/${stageId}/documents/convocation-list`
      )
      const file = await session.api.download(`/admin/official-documents/${doc.id}/pdf`)
      saveFile(file, `emargement-${doc.code}.pdf`)
    },
    onSuccess: invalidate,
    onError: (error) => toast.error(errorMessage(error)),
  })
  const zip = useMutation({
    mutationFn: async (id: string) =>
      saveFile(await session.api.download(`/admin/document-batches/${id}/zip`), 'convocations.zip'),
    onError: (error) => toast.error(errorMessage(error)),
  })

  const open = async (row: ConvocationRow) => {
    setPreview({ row, blob: null })
    try {
      const file = await session.api.download(`/admin/convocations/${row.id}/pdf`)
      setPreview({ row, blob: file.blob })
      await invalidate()
    } catch (error) {
      setPreview({ row, blob: null, error: errorMessage(error) })
    }
  }

  const submit = (e: FormEvent) => {
    e.preventDefault()
    if (o?.summary.draft) setModal('redistribute')
    else distribute.mutate()
  }

  if (overview.isError) return <Alert tone="danger">{errorMessage(overview.error)}</Alert>
  if (!o)
    return (
      <Card>
        <CardBody>Chargement des convocations…</CardBody>
      </Card>
    )

  const running = o.batches.find((b) => b.status === 'queued' || b.status === 'running')
  const lastBatch = o.batches[0]
  const issued = o.summary.issued > 0
  const columns: Column<ConvocationRow>[] = [
    {
      key: 'number',
      header: 'Dossier',
      cell: (r) => <span className="mono">{r.applicationNumber}</span>,
    },
    { key: 'name', header: 'Candidat', cell: (r) => r.name },
    {
      key: 'place',
      header: 'Lieu',
      cell: (r) => (
        <span className="stack" style={{ gap: 2 }}>
          <span>{r.center}</span>
          <span className="muted">
            {r.room} · place {r.seat}
          </span>
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Statut',
      cell: (r) => <Badge variant={CONVOCATION_VARIANT[r.status]}>{r.statusLabel}</Badge>,
    },
    {
      key: 'seen',
      header: 'Consultée',
      cell: (r) => (r.downloadedAt ? when(r.downloadedAt) : <span className="muted">—</span>),
    },
    ...(canGenerate
      ? [
          {
            key: 'pdf',
            header: <span className="sr-only">Aperçu</span>,
            align: 'end' as const,
            cell: (r: ConvocationRow) => (
              <Button
                size="sm"
                variant="ghost"
                aria-label={`Aperçu de la convocation de ${r.name}`}
                onClick={(e) => {
                  e.stopPropagation()
                  void open(r)
                }}
              >
                <Eye aria-hidden="true" size={16} />
              </Button>
            ),
          },
        ]
      : []),
  ]

  return (
    <>
      <StatGrid>
        <Stat label="Candidats de l’étape" value={o.population} />
        <Stat label="Convocations préparées" value={o.summary.draft} />
        <Stat
          label="PDF générés"
          value={`${o.summary.withDocument} / ${o.summary.draft + o.summary.issued}`}
        />
        <Stat label="Envoyées" value={o.summary.issued} />
      </StatGrid>

      {canGenerate && (
        <Card>
          <CardHeader title="Répartition dans les salles" />
          <CardBody>
            {issued ? (
              <Alert tone="info">
                Les convocations ont été envoyées : elles ne se modifient plus. Pour une nouvelle
                répartition, annulez-les d’abord (motif obligatoire, candidats prévenus).
              </Alert>
            ) : centers.data?.length === 0 ? (
              <Alert tone="warning">
                Aucun centre d’examen.{' '}
                <Link className="link" to={`/documents/concours/${competitionId}`}>
                  Configurer les centres et salles
                </Link>
              </Alert>
            ) : (
              <form className="stack" onSubmit={submit} noValidate>
                <div className="grid-2">
                  <Select
                    label="Ordre de répartition"
                    options={MODE_OPTIONS}
                    value={mode}
                    error={errors.mode}
                    onChange={(e) => setMode(e.target.value as DistributionMode)}
                  />
                  <Input
                    label="Date et heure de convocation"
                    type="datetime-local"
                    required
                    value={scheduled}
                    error={errors.scheduledAt}
                    hint="Heure de Dakar (GMT)."
                    onChange={(e) => setDate(e.target.value)}
                  />
                </div>
                <fieldset className="stack" style={{ gap: 'var(--space-2)' }}>
                  <legend className="field-label">Centres utilisés</legend>
                  {centers.data?.map((c) => (
                    <label key={c.id} className="check-row">
                      <input
                        type="checkbox"
                        checked={selected.includes(c.id)}
                        onChange={(e) =>
                          setChosen(
                            e.target.checked
                              ? [...selected, c.id]
                              : selected.filter((id) => id !== c.id)
                          )
                        }
                      />
                      <span>
                        {c.name} <span className="muted">· {c.capacity} places</span>
                      </span>
                    </label>
                  ))}
                  <span className={capacity < o.population ? 'field-error' : 'muted'}>
                    Capacité retenue : {capacity} places pour {o.population} candidat(s).
                  </span>
                </fieldset>
                <div className="row" style={{ justifyContent: 'flex-end' }}>
                  <Button
                    variant="primary"
                    type="submit"
                    loading={distribute.isPending}
                    disabled={!selected.length || !o.population}
                  >
                    <Shuffle aria-hidden="true" size={16} />
                    {o.summary.draft ? 'Répartir à nouveau' : 'Répartir les candidats'}
                  </Button>
                </div>
              </form>
            )}
          </CardBody>
        </Card>
      )}

      <Card>
        <CardHeader
          title="Convocations"
          actions={
            <span className="row" style={{ gap: 'var(--space-2)', flexWrap: 'wrap' }}>
              {canGenerate && o.convocations.length > 0 && (
                <>
                  <Button
                    size="sm"
                    variant="secondary"
                    loading={batch.isPending}
                    disabled={Boolean(running)}
                    onClick={() => batch.mutate()}
                  >
                    <FileArchive aria-hidden="true" size={16} />
                    Générer tous les PDF
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    loading={list.isPending}
                    onClick={() => list.mutate()}
                  >
                    <FileText aria-hidden="true" size={16} />
                    Liste d’émargement
                  </Button>
                </>
              )}
              {canSend && o.summary.draft > 0 && (
                <Button size="sm" variant="primary" onClick={() => setModal('issue')}>
                  <Send aria-hidden="true" size={16} />
                  Envoyer aux candidats
                </Button>
              )}
              {canSend && o.convocations.length > 0 && (
                <Button size="sm" variant="danger" onClick={() => setModal('cancel')}>
                  <XCircle aria-hidden="true" size={16} />
                  Annuler
                </Button>
              )}
            </span>
          }
        />
        {lastBatch && (
          <CardBody>
            <div className="batch-status" aria-live="polite">
              <span>
                Génération groupée : <strong>{BATCH_LABELS[lastBatch.status]}</strong>{' '}
                <span className="muted">({when(lastBatch.createdAt)})</span>
              </span>
              {(lastBatch.status === 'queued' || lastBatch.status === 'running') && (
                <Progress
                  done={lastBatch.done}
                  total={lastBatch.total}
                  label="Avancement de la génération"
                />
              )}
              {lastBatch.status === 'failed' && (
                <span className="field-error">{lastBatch.error}</span>
              )}
              {lastBatch.ready && (
                <Button
                  size="sm"
                  variant="secondary"
                  loading={zip.isPending}
                  onClick={() => zip.mutate(lastBatch.id)}
                >
                  <Download aria-hidden="true" size={16} />
                  Télécharger le ZIP ({lastBatch.total} fichiers)
                </Button>
              )}
            </div>
          </CardBody>
        )}
        <Table
          columns={columns}
          rows={o.convocations}
          rowKey={(r) => r.id}
          caption="Convocations de l’étape"
          empty={
            <Empty
              title="Aucune convocation"
              description="Répartissez les candidats dans les salles pour préparer les convocations."
            />
          }
        />
      </Card>

      <DocumentPreviewModal
        open={preview !== null}
        onClose={() => setPreview(null)}
        title={preview ? `Convocation — ${preview.row.name}` : 'Convocation'}
        description={
          preview && (
            <>
              {preview.row.center} · {preview.row.room} · place {preview.row.seat}
            </>
          )
        }
        blob={preview?.blob ?? null}
        error={preview?.error}
        fileName={preview ? `convocation-${preview.row.applicationNumber}.pdf` : undefined}
      />
      <ConfirmModal
        open={modal === 'redistribute'}
        onClose={() => setModal(null)}
        onConfirm={() => distribute.mutate()}
        title="Répartir à nouveau ?"
        message="Les convocations préparées (non envoyées) sont remplacées et leurs PDF révoqués."
        confirmLabel="Répartir à nouveau"
      />
      <ConfirmModal
        open={modal === 'issue'}
        onClose={() => setModal(null)}
        onConfirm={() => issue.mutate()}
        title="Envoyer les convocations ?"
        message={`${o.summary.draft} candidat(s) recevront un courriel et pourront télécharger leur convocation dans leur espace. Une convocation envoyée ne se modifie plus.`}
        confirmLabel="Envoyer"
      />
      <ConfirmModal
        open={modal === 'cancel'}
        onClose={() => setModal(null)}
        onConfirm={(reason) => cancel.mutate(reason ?? '')}
        tone="danger"
        reasonRequired
        reasonLabel="Motif de l’annulation"
        title="Annuler les convocations de l’étape ?"
        message="Les PDF sont révoqués (la vérification les signalera comme révoqués) et les candidats déjà convoqués sont prévenus."
        confirmLabel="Annuler les convocations"
      />
    </>
  )
}

function StageDocumentsSection({
  stageId,
  canGenerate,
  canPv,
  canResults,
}: {
  stageId: string
  canGenerate: boolean
  canPv: boolean
  canResults: boolean
}) {
  const { session } = useAuth()
  const toast = useToast()
  const queryClient = useQueryClient()
  const docs = useStageDocuments(stageId, canGenerate || canPv || canResults)
  const readable = (d: OfficialDocument) =>
    d.kind === 'convocation_list'
      ? canGenerate
      : d.kind === 'commission_report'
        ? canPv
        : d.kind === 'results_list'
          ? canResults
          : false

  const generate = useMutation({
    mutationFn: async (kind: 'commission-report' | 'results-list') => {
      const doc = await session.api.post<OfficialDocument>(
        `/admin/stages/${stageId}/documents/${kind}`
      )
      saveFile(
        await session.api.download(`/admin/official-documents/${doc.id}/pdf`),
        `${doc.kind}-${doc.code}.pdf`
      )
      return doc
    },
    onSuccess: async (doc) => {
      toast.success(`${doc.kindLabel} : ${doc.code}`)
      await queryClient.invalidateQueries({ queryKey: DOCUMENTS_KEY })
    },
    onError: (error) => toast.error(errorMessage(error)),
  })
  const download = useMutation({
    mutationFn: async (d: OfficialDocument) =>
      saveFile(
        await session.api.download(`/admin/official-documents/${d.id}/pdf`),
        `${d.kind}-${d.code}.pdf`
      ),
    onError: (error) => toast.error(errorMessage(error)),
  })

  const columns: Column<OfficialDocument>[] = [
    {
      key: 'title',
      header: 'Document',
      cell: (d) => (
        <span className="stack" style={{ gap: 2 }}>
          <strong>{d.kindLabel}</strong>
          <span className="muted">{d.title}</span>
        </span>
      ),
    },
    { key: 'code', header: 'Code', cell: (d) => <span className="mono">{d.code}</span> },
    { key: 'date', header: 'Produit le', cell: (d) => when(d.generatedAt) },
    {
      key: 'state',
      header: 'État',
      cell: (d) =>
        d.revokedAt ? (
          <Badge variant="danger" title={d.revokedReason ?? undefined}>
            Révoqué
          </Badge>
        ) : (
          <Badge variant="success">En vigueur</Badge>
        ),
    },
    {
      key: 'dl',
      header: <span className="sr-only">Télécharger</span>,
      align: 'end',
      cell: (d) =>
        readable(d) && (
          <Button
            size="sm"
            variant="ghost"
            aria-label={`Télécharger ${d.kindLabel} ${d.code}`}
            onClick={() => download.mutate(d)}
          >
            <Download aria-hidden="true" size={16} />
          </Button>
        ),
    },
  ]

  return (
    <Card>
      <CardHeader
        title="Documents officiels de l’étape"
        actions={
          <span className="row" style={{ gap: 'var(--space-2)', flexWrap: 'wrap' }}>
            {canPv && (
              <Button
                size="sm"
                variant="secondary"
                loading={generate.isPending && generate.variables === 'commission-report'}
                onClick={() => generate.mutate('commission-report')}
              >
                <FileText aria-hidden="true" size={16} />
                PV de commission
              </Button>
            )}
            {canResults && (
              <Button
                size="sm"
                variant="secondary"
                loading={generate.isPending && generate.variables === 'results-list'}
                onClick={() => generate.mutate('results-list')}
              >
                <FileText aria-hidden="true" size={16} />
                Liste des résultats
              </Button>
            )}
          </span>
        }
      />
      {docs.isError && (
        <CardBody>
          <Alert tone="danger">{errorMessage(docs.error)}</Alert>
        </CardBody>
      )}
      <Table
        columns={columns}
        rows={docs.data ?? []}
        rowKey={(d) => d.id}
        loading={docs.isPending}
        caption="Documents officiels de l’étape"
        empty={
          <Empty
            title="Aucun document produit"
            description="PV de commission (relevés tous validés), liste d’émargement, liste des résultats publiés."
          />
        }
      />
    </Card>
  )
}
